import isEqual from "lodash.isequal";

const numberOrNA = (input) => (input === "n/a" ? null : parseInt(input));

const stringOrNA = (input) => (input === "n/a" ? null : String(input));

const _LIST_FIELDS = [
  { name: "name", parse: (d) => String(d) },
  { name: "id", parse: stringOrNA },
  { name: "source", parse: (d) => String(d) },
  { name: "totalSubs", parse: numberOrNA },
  { name: "activeSubs", parse: numberOrNA },
  { name: "currentSubs", parse: numberOrNA },
  { name: "inactivity", parse: numberOrNA },
];

const fieldValue = (source, { name, parse }) => [name, parse(source)];

const asPolicyListItem = (row) =>
  Object.fromEntries(
    row
      .split(/\s+/)
      .map((source, index) => fieldValue(source, _LIST_FIELDS[index]))
  );

export const parsePolicyList = (response) => {
  const [_head, ...rows] = response.trim("\n").split("\n");
  return rows.map(asPolicyListItem);
};

const _DETAIL_FIELDS = [
  { from: "Blocking", name: "blocking", parse: (d) => String(d) },
  { from: "Auto-Congestion-Management", name: "acm", parse: (d) => String(d) },
  {
    from: "RatePerSubs downlink",
    name: "ratePerSubsDown",
    parse: (d) => String(d),
  },
  {
    from: "RatePerSubs uplink",
    name: "ratePerSubsUp",
    parse: (d) => String(d),
  },
  {
    from: "  Rate",
    name: (_value, prev) =>
      // Asumes that rate will be inmediatelly after RatePerSubs <Any>.
      prev.ratePerSubsUp === "yes"
        ? "ratePerSubsUp"
        : prev.ratePerSubsDown === "yes"
        ? "ratePerSubsDown"
        : "rate",
    parse: (d) => (isNaN(Number(d)) ? d : Number(d)),
  },
];

const stateField = ({ name, parse }, value, prev) => {
  return {
    [typeof name === "function" ? name(value, prev) : name]: parse(value, prev),
  };
};

const asTuple = (line) => line.split(/:\s*/);

const givenFields = (fields = _DETAIL_FIELDS) => {
  const fieldByFrom = Object.fromEntries(
    fields.map(({ from, ...field }) => [from, field])
  );
  const fillFields = (prev, [from, value]) =>
    from in fieldByFrom === false
      ? prev
      : { ...prev, ...stateField(fieldByFrom[from], value, prev) };
  const extract = (response) =>
    response.trim("\n").split("\n").map(asTuple).reduce(fillFields, {});
  return { extract };
};

export const parsePolicyDetails = (response) => givenFields().extract(response);

const warningRE = new RegExp("%WARN-EINVAL:\\s(.*)");

export const handleWarnings = (input) => {
  const match = input.match(warningRE);
  if (match !== null) {
    const [_all, message] = match;
    throw message;
  }
  return input;
};

const checkAttrsSet = new Set([
  "ID",
  "ASSIGNMENT",
  "BLOCK",
  "LIMIT-DN",
  "LIMIT-UP",
  "FLOW-LIMIT",
  "ACM",
]);
const sumAttrsSet = new Set([
  "SUBS-PROVISIONED",
  "SUBS-ACTIVE",
  "MBYTES-UPLINK",
  "MBYTES-DOWNLINK",
]);

const doCheckBqns = (values, bqns) => {
  const bqnsInPolicy = values.map(({ bqn }) => bqn);
  const bqnsArr = bqns.map(({ bqnId }) => String(bqnId));
  return bqnsInPolicy.sort().toString() === bqnsArr.sort().toString();
};

const doCheckPolicyValues = (values) => {
  const allEqual = values.every(({ policy }) => {
    return isEqual(policy, values[0].policy);
  });
  if (!allEqual) {
    return false;
  }

  return values.reduce((acc, { sum }) => {
    for (const key in sum) {
      if (sum[key] === "n/a") {
        if (!acc[key]) {
          acc[key] = "n/a";
        }
      } else {
        if (acc[key] === "n/a") {
          acc[key] = parseFloat(sum[key]);
        } else if (!acc[key]) {
          acc[key] = parseFloat(sum[key]);
        } else {
          acc[key] = acc[key] + parseFloat(sum[key]);
        }
      }
    }
    return acc;
  }, {});
};

export const getFilteredResponse = ({ response, bqns, mode, bqnId }) => {
  if (mode === "standalone") {
    const [header, ...rows] = response.trim().split("\n");
    if (rows.length === 0) {
      return [];
    }

    const headerAttrs = header.trim().split(/\s+/);
    return rows.map((row) => {
      const rowAttrs = row.trim(/\s/).split(/\s+/);
      const newEntry = rowAttrs.reduce((acc, attr, index) => {
        acc[headerAttrs[index]] = attr;
        return acc;
      }, {});
      return newEntry;
    });
  };

  const bqnsOnline = bqns.filter(({ function: functionBqn }) => functionBqn !== "offline");
  const [header, ...rows] = response.trim(/\s/).split("\n");
  const headerAttrs = header.trim(/\s/).split(/\s+/);
  const indexHeaderMap = headerAttrs.reduce((acc, value, index) => {
    acc[value] = index;
    return acc;
  }, {});
  const policies = rows.reduce((acc, row) => {
    const rowAttrs = row.trim().split(/\s+/);
    const name = rowAttrs[indexHeaderMap["POLICY"]];
    const bqn = rowAttrs[indexHeaderMap["BQN"]];
    const newItem = rowAttrs.reduce((accItem, attr, index) => {
      if (checkAttrsSet.has(headerAttrs[index])) {
        accItem.policy = { ...accItem.policy, [headerAttrs[index]]: attr };
      }
      if (sumAttrsSet.has(headerAttrs[index])) {
        accItem.sum = { ...accItem.sum, [headerAttrs[index]]: attr };
      }
      return accItem;
    }, {});
    if (!acc[name]) {
      acc[name] = [{ ...newItem, bqn }];
    } else {
      acc[name].push({ ...newItem, bqn });
    }
    return acc;
  }, {});

  const foundAllPolicies = Object.entries(policies).reduce(
    (acc, [name, value]) => {
      if (value.length === bqnsOnline.length) {
        const hasAllBqns = doCheckBqns(value, bqnsOnline);
        if (hasAllBqns) {
          const hasSamePolicyValues = doCheckPolicyValues(value);
          if (hasSamePolicyValues) {
            acc[name] = hasSamePolicyValues;
          }
        }
      }
      return acc;
    },
    {}
  );
  const newPolicies = Object.entries(policies).reduce(
    (acc, [name, policyValues]) => {
      if (!foundAllPolicies[name]) {
        const rows = policyValues.map(({ policy, bqn, sum }) => {
          acc.rows.push({ ...policy, ...sum, ["POLICY"]: name, ["BQN"]: bqn });
        });
      } else {
        if (!acc.foundNames.has(name)) {
          const assignment = policyValues[0].policy.ASSIGNMENT;
          const newBqn = assignment === 'configured' ? 'all' : bqnsOnline.length > 1 ? "all" : policyValues[0].bqn;
          acc.foundNames.add(name);
          acc.rows.push({
            ...policyValues[0].policy,
            ...foundAllPolicies[name],
            ["BQN"]: newBqn,
            ["POLICY"]: name,
          });
        }
      }
      return acc;
    },
    { rows: [], foundNames: new Set([]) }
  );

  const filteredRows =
    bqnId === "all"
      ? newPolicies.rows
      : newPolicies.rows.filter(
          (row) =>
            parseFloat(row.BQN) === parseFloat(bqnId) || row.BQN === "all"
        );

  const newRows = filteredRows.map((row) => {
    return headerAttrs.map((attr) => row[attr]).join("    ");
  });

  return filteredRows;
};

